import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLocalResponse } from './siteChatLocal';
import { handleSiteChatAPI } from './siteChatAPI';
import './SiteChatWidget.css';

const SiteChatWidget = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceReadOn, setVoiceReadOn] = useState(true);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [messages, setMessages] = useState([]);
  const messagesEndRef = useRef(null);
  const transcriptRef = useRef('');
  const recognitionRef = useRef(null);
  const spokenTextRef = useRef('');

  // Initialize Speech Recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const speechSynthesisAvailable = !!window.speechSynthesis && !!window.SpeechSynthesisUtterance;
    const supported = !!SpeechRecognition && speechSynthesisAvailable;
    setSpeechSupported(supported);
    if (!supported) {
      setVoiceReadOn(false);
    }

    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.language = 'en-US';
      recognitionRef.current.interimResults = false;
      recognitionRef.current.continuous = false;

      recognitionRef.current.onstart = () => {
        setIsListening(true);
      };

      recognitionRef.current.onresult = (event) => {
        let transcript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        transcriptRef.current = transcript;
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
        if (transcriptRef.current.trim()) {
          askQuestion(transcriptRef.current.trim());
          transcriptRef.current = '';
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };
    }
  }, []);

  // Load from sessionStorage on mount
  useEffect(() => {
    const savedState = sessionStorage.getItem('ai-assistant-chat-state');
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        setMessages(state.messages || []);
        setIsOpen(state.isOpen || false);
      } catch (e) {
        console.error('Error loading chat state:', e);
      }
    }
  }, []);

  // Save to sessionStorage on change
  useEffect(() => {
    sessionStorage.setItem('ai-assistant-chat-state', JSON.stringify({ isOpen, messages }));
  }, [isOpen, messages]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const stopSpeaking = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
    spokenTextRef.current = '';
  };

  const toggleVoiceRead = () => {
    if (!speechSupported) return;
    if (isSpeaking) {
      stopSpeaking();
    }
    setVoiceReadOn(!voiceReadOn);
  };

  const startListening = () => {
    if (!speechSupported || !recognitionRef.current || isListening || isSpeaking || loading) return;
    recognitionRef.current.start();
  };

  const readResponseAloud = (text) => {
    if (!voiceReadOn) return;

    // Clean text: remove URLs and content in parentheses
    const cleanedText = text
      .replace(/https?:\/\/[^\s]+/g, '')
      .replace(/\([^)]*\)/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    if (!cleanedText) return;

    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
    }

    const utterance = new SpeechSynthesisUtterance(cleanedText);
    utterance.rate = 1;
    utterance.pitch = 1;

    utterance.onstart = () => {
      setIsSpeaking(true);
      spokenTextRef.current = cleanedText;
    };

    utterance.onend = () => {
      setIsSpeaking(false);
      spokenTextRef.current = '';
    };

    utterance.onerror = () => {
      setIsSpeaking(false);
      spokenTextRef.current = '';
    };

    window.speechSynthesis.speak(utterance);
  };

  const askQuestion = async (userQuestion) => {
    const trimmedQuestion = userQuestion.trim();
    if (!trimmedQuestion) return;

    // Check for restricted keywords
    const restrictedKeywords = ['medical', 'health', 'doctor', 'medication', 'prescription', 'disease'];
    const containsRestricted = restrictedKeywords.some(keyword =>
      trimmedQuestion.toLowerCase().includes(keyword)
    );

    if (containsRestricted) {
      const botMessage = {
        role: 'bot',
        text: 'I can help you with questions about Yumway food products, services, exports, and company information. For medical or health-related queries, please consult appropriate professionals.',
      };
      setMessages((prev) => [...prev, { role: 'user', text: trimmedQuestion }, botMessage]);
      setQuestion('');
      readResponseAloud(botMessage.text);
      return;
    }

    // Add user message
    setMessages((prev) => [...prev, { role: 'user', text: trimmedQuestion }]);
    setQuestion('');
    setLoading(true);

    try {
      // Try local response first for quick intents
      const localResponse = getLocalResponse(trimmedQuestion);
      if (localResponse) {
        const botMessage = {
          role: 'bot',
          text: localResponse.answer,
          links: localResponse.links,
        };
        setMessages((prev) => [...prev, botMessage]);
        readResponseAloud(localResponse.answer);
        setLoading(false);
        return;
      }

      // Use API handler for complex queries
      const response = await handleSiteChatAPI(trimmedQuestion);
      const botMessage = {
        role: 'bot',
        text: response.answer,
        links: response.links,
        needsClarification: response.needsClarification,
      };
      setMessages((prev) => [...prev, botMessage]);
      readResponseAloud(response.answer);
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = {
        role: 'bot',
        text: 'I apologize for the technical issue. Please try again or contact us directly at support@icedreamglobal.com or call (+91) 9873578910.',
      };
      setMessages((prev) => [...prev, errorMessage]);
      readResponseAloud(errorMessage.text);
    }

    setLoading(false);
  };

  const handleSendMessage = () => {
    if (question.trim() && !loading && !isListening && !isSpeaking) {
      askQuestion(question);
    }
  };

  const handleLinkClick = (event, link) => {
    if (link.href.startsWith('http')) {
      return;
    }
    event.preventDefault();
    if (link.href.startsWith('/')) {
      navigate(link.href);
    } else {
      window.location.href = link.href;
    }
  };

  const isInputLocked = isListening || isSpeaking || loading;

  return (
    <div className="site-chat-widget">
      {/* Chat Button */}
      <button
        className="chat-button"
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) {
            if (isSpeaking) stopSpeaking();
          }
        }}
        aria-label="Open chat"
        title="Yumway Chat"
      >
        <i className="bi bi-chat-dots-fill"></i>
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className={`chat-window ${isMobile ? 'mobile' : 'desktop'}`}>
          {/* Header */}
          <div className="chat-header">
            <div className="header-left">
              <i className="bi bi-chat-dots"></i>
              <div>
                <span>Yumway Assistant</span>
              </div>
            </div>
            
            <div className="header-right">
                      <button
                className="icon-btn11"
                onClick={toggleVoiceRead}
                title={speechSupported ? (voiceReadOn ? 'Turn off voice reading' : 'Turn on voice reading') : 'Voice assistant not available'}
                disabled={!speechSupported}
              >
                <i className={`bi bi-${voiceReadOn ? 'volume-up-fill' : 'volume-mute-fill'}`}></i>
              </button>
              <button
                className="icon-btn11"
                onClick={() => {
                  setIsOpen(false);
                  if (isSpeaking) stopSpeaking();
                }}
                title="Close chat"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="messages-area">
            {messages.length === 0 && (
              <div className="welcome-message">
                <h3>Welcome to Yumway Assistant! 👋</h3>
                <p>Ask me about our premium food products, exports, quality, or contact details. I can point you to the right page or direct you to Contact Us.</p>
              </div>
            )}
            {messages.map((msg, index) => (
              <div key={index} className={`message message-${msg.role}`}>
                <div className="message-bubble">
                  <p>{msg.text}</p>
                  {msg.links && msg.links.length > 0 && (
                    <div className="message-links">
                      {msg.links.map((link, linkIndex) => (
                        <div key={linkIndex} className="link-item">
                          <a
                            href={link.href}
                            {...(link.href.startsWith('http')
                              ? { target: '_blank', rel: 'noopener noreferrer' }
                              : {})}
                            onClick={(e) => handleLinkClick(e, link)}
                          >
                            👉 {link.title}
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="message message-bot">
                <div className="message-bubble typing">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Voice Control Buttons */}
          {speechSupported && (
            <div className="voice-controls">
              <button
                className="voice-btn start-voice"
                onClick={startListening}
                disabled={isListening || isSpeaking || loading}
              >
                <i className="bi bi-mic-fill"></i>
                {isListening ? 'Listening...' : 'Start Voice Assistant'}
              </button>
              <button
                className="voice-btn stop-reading"
                onClick={stopSpeaking}
                disabled={!isSpeaking}
              >
                <i className="bi bi-stop-circle"></i>
                Stop Reading
              </button>
            </div>
          )}

          {/* Input Area */}
          <div className="input-area">
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="Ask about products, exports, quality, or contact..."
              disabled={isInputLocked}
              className={isInputLocked ? 'locked' : ''}
            />
            <button
              onClick={handleSendMessage}
              disabled={isInputLocked || !question.trim()}
              className={isInputLocked ? 'locked' : ''}
            >
              {loading ? '...' : isListening ? 'Mic On' : isSpeaking ? 'Reading...' : 'Send'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteChatWidget;
